import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import s from './Features.module.css';

gsap.registerPlugin(ScrollTrigger);

export default function Features() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const imageRef = useRef(null);
  const badgesRef = useRef([]);

  const features = [
    {
      title: 'Téléchargement d\'Images',
      description: 'Analysez rapidement vos images de bétail avec un téléchargement fluide.',
      position: { top: '4%', left: '50%', transform: 'translate(-50%, -50%)' }
    },
    {
      title: 'Détection IA Avancée',
      description: 'Modèles IA optimisés pour la détection et le comptage automatisés du bétail.',
      position: { top: '28%', left: '95%', transform: 'translate(-50%, -50%)' }
    },
    {
      title: 'Analyses en Temps Réel',
      description: 'Résultats instantanés et indicateurs clairs pour vos opérations de surveillance.',
      position: { top: '64%', left: '95%', transform: 'translate(-50%, -50%)' }
    },
    {
      title: 'Génération de Rapports',
      description: 'Exportez des rapports CSV structurés pour partager facilement vos découvertes.',
      position: { top: '95%', left: '50%', transform: 'translate(-50%, -50%)' }
    },
    {
      title: 'Tableau de Bord Intuitif',
      description: 'Une interface propre pour gérer vos zones, projets et résultats en un seul endroit.',
      position: { top: '64%', left: '5%', transform: 'translate(-50%, -50%)' }
    },
    {
      title: 'Sécurité et Authentification',
      description: 'Connexion protégée pour garantir la confidentialité de vos données d\'analyse.',
      position: { top: '28%', left: '5%', transform: 'translate(-50%, -50%)' }
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      const isMobile = window.innerWidth <= 768;
      const actions = "play none none reverse";
      gsap.to(sectionRef.current, {
        backgroundColor: " #f8f9fa", 
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 50%", 
          end: "top 30%",   
          scrub: true,      
        }
      });
      // 1. Animation du texte (Titre et Sous-titre)
      gsap.from([titleRef.current, subtitleRef.current], {
        y: 50,
        opacity: 0,
        duration: 1,
        stagger: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: titleRef.current,
          start: "top 85%",
          toggleActions: actions,
        }
      });

      // 2. Animation de l'image centrale

      if (!isMobile) {
        gsap.from(imageRef.current, {
            scale: 0.8,
            opacity: 0,
            duration: 1.2,
            ease: "back.out(1.7)",
            scrollTrigger: {
            trigger: imageRef.current,
            start: "top 80%",
            toggleActions: actions,
            }
        });
      }

      // 3. Animation des badges (Effet de vol avec rotation et rebond)
    badgesRef.current.forEach((badge, index) => {
      if (!badge) return;

      // Logique Responsive pour l'animation
      const animProps = isMobile 
        ? { y: 30, x: 0, opacity: 0, duration: 0.8 } 
        : { 
            x: index === 1 || index === 2 ? 100 : index === 4 || index === 5 ? -100 : 0,
            y: index === 0 ? -100 : index === 3 ? 100 : 0,
            rotation: index % 2 === 0 ? 10 : -10,
            opacity: 0,
            duration: 1.5,
            ease: "elastic.out(1, 0.75)"
          };

      gsap.from(badge, {
        ...animProps,
        delay: isMobile ? index * 0.1 : 0.2, 
        scrollTrigger: {
          trigger: badge,
          start: "top 90%",
          toggleActions: actions,
        }
      });
    });
    }, sectionRef);

    return () => ctx.revert(); 
  }, []);

  return (
    <section ref={sectionRef} id="features" className={s.featuresSection}>
      <div className={s.container}>
        <div className={s.topText}>
          <h2 ref={titleRef} className={s.featuresTitle}>Fonctionnalités Clés</h2>
          <p ref={subtitleRef} className={s.featuresSubtitle}>
            Découvrez les outils puissants de notre plateforme de détection de bétail alimentée par l'IA.
          </p>
        </div>

        <div className={s.featureShowcase}>
          <div className={s.visualCenter}>
            <div ref={imageRef} className={s.imageWrapper}>
              <img
                src="/images/logo.png"
                alt="Vue centrale du système de détection"
                className={s.heroImage}
              />
            </div>

            <div className={s.badgeArea}>
              {features.map((feature, index) => (
                <div
                  key={index}
                  ref={(el) => (badgesRef.current[index] = el)}
                  className={s.featureBadge}
                  style={feature.position}
                >
                  <h3 className={s.badgeTitle}>{feature.title}</h3>
                  <p className={s.badgeDescription}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}